<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered) },
      skin === 'dark' ? 'menu-dark' : 'menu-light'
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row justify-content-center logo-container">
          <li class="nav-item logo-item">
            <div class="logo-wrapper" :class="{ 'logo-collapsed': isVerticalMenuCollapsed && !isMouseHovered }" @click="navigateHome" style="cursor: pointer;">
              <!-- Colapsado: ícone pequeno ou fallback de inicial -->
              <template v-if="isVerticalMenuCollapsed && !isMouseHovered">
                <img
                  v-if="tenantLogoUrl"
                  :src="tenantLogoUrl"
                  :alt="tenantNome"
                  width="46"
                  class="logo-icon-img"
                  @error="onLogoError"
                />
                <div v-else class="logo-icon-fallback">
                  {{ tenantNome.charAt(0).toUpperCase() }}
                </div>
              </template>
              <!-- Expandido: logo completo + nome -->
              <template v-else>
                <div class="logo-full-wrapper">
                  <img
                    v-if="tenantLogoUrl && !logoFailed"
                    :src="tenantLogoUrl"
                    :alt="tenantNome"
                    height="54"
                    class="logo-full-img"
                    @error="onLogoError"
                  />
                  <span class="logo-tenant-name" :class="{ 'logo-tenant-name--solo': !tenantLogoUrl || logoFailed }">
                    {{ tenantNome }}
                  </span>
                </div>
              </template>
            </div>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div
      :class="{'d-block': shallShadowBottom}"
      class="shadow-bottom"
    />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area mt-3"
      tagname="ul"
      @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
    >
      <vertical-nav-menu-items
        :items="navMenuItems"
        class="navigation navigation-main"
      />
    </vue-perfect-scrollbar>
    <!-- /main menu content-->
  </div>
</template>

<script>
import { buildNavMenuItems } from '@/navigation/vertical';
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import { BLink, BImg } from 'bootstrap-vue';
import { provide, computed, ref } from '@vue/composition-api';
import useAppConfig from '@core/app-config/useAppConfig';
import { $themeConfig } from '@themeConfig';
import LogoApp from '@/components/LogoApp.vue';
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue';
import useVerticalNavMenu from './useVerticalNavMenu';

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
    LogoApp,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props);

    const { skin } = useAppConfig();

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false);

    provide('isMouseHovered', isMouseHovered);

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    };

    const collapseTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'));

    // App Name
    const { appName, appLogoImage } = $themeConfig.app;

    const navMenuItems = computed(() => buildNavMenuItems());

    return {
      navMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,
    };
  },
  data() {
    return {
      logoFailed: false,
    };
  },
  computed: {
    tenantLogoUrl() {
      const url = this.$store.getters['tenantStore/logoUrl'];
      if (url) this.logoFailed = false;
      return url;
    },
    tenantNome() {
      return this.$store.getters['tenantStore/tenantNome'];
    },
  },
  methods: {
    navigateHome() {
      this.$router.push('/home');
    },
    onLogoError() {
      this.logoFailed = true;
    },
  },
};
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";

// Logo area no cabeçalho do sidebar
.logo-full-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px;
  min-width: 0;
}

.logo-full-img {
  max-height: 54px;
  width: auto;
  max-width: 140px;
  object-fit: contain;
  flex-shrink: 0;
}

.logo-tenant-name {
  font-size: 13px;
  font-weight: 700;
  color: var(--sf-sidebar-text, #1e293b);
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.2;
  max-width: 120px;
  letter-spacing: 0.3px;

  &--solo {
    font-size: 14px;
    max-width: 150px;
  }
}

.logo-icon-fallback {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: var(--primary, #1976d2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: 18px;
}
</style>
